import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Layout } from '@utilities';

import Addons from './components/Addons';

import { getCartCost, getDisplayCurrency } from './utilities/helpers';

import styles from './_index.module.scss';

const Cart = ({
  addons,
  footnote,
  hasCaveats,
  items,
  onAddonChange,
  optionalAddons,
  paymentOption,
  promos,
  totalText = 'Total',
}) => {
  const displayedOptionalAddons = useMemo(() => {
    // Filter out any optional add-ons that are already in the required add-ons
    return (
      optionalAddons?.filter(
        ({ addonType }) => !addons?.some((addon) => addon.addonType === addonType)
      ) || []
    );
  }, [addons, optionalAddons]);

  const totalDue = getCartCost({
    addons,
    currency: paymentOption.currency,
    items,
    optionalAddons,
    promos,
  });

  if (!items?.length) {
    return (
      <div className={styles['cart-items']}>
        <p>No items in your cart</p>
      </div>
    );
  }

  return (
    <Layout.Flex.Column as="section" className={styles['cart']}>
      <h3>Standard Costs</h3>
      <Layout.Flex.Column as="ul" className={styles['cart-items']}>
        {items.map((item, index) => {
          const value = item.prices?.[paymentOption.currency] ?? 0;
          // Use stable identifier for key (id if available, otherwise name)
          const itemKey = item.id ? `cart-item-${item.id}` : `cart-item-${item.name}`;

          return (
            <Fragment key={itemKey}>
              {index > 0 && (
                <li aria-hidden="true">
                  <hr />
                </li>
              )}
              <li data-testid="cart-item">
                <Layout.Flex gap="x-large">
                  <Layout.Fill>
                    <h5 className={styles['cart-item-name']}>{item.name}</h5>
                    {item.description && <div>{item.description}</div>}
                  </Layout.Fill>
                  <div className={styles['cart-item-price']}>
                    {`${value} ${getDisplayCurrency({ paymentOption, value })}${hasCaveats ? '*' : ''}`}
                  </div>
                </Layout.Flex>
              </li>
            </Fragment>
          );
        })}
      </Layout.Flex.Column>

      {addons?.length > 0 && (
        <div>
          <h3>Add-ons</h3>
          <Layout.Flex.Column as="ul" className={styles['cart-items']}>
            {addons.map((addon, index) => {
              const value = addon.prices[paymentOption.currency] ?? 0;

              return (
                <Fragment key={addon.id}>
                  {index > 0 && (
                    <li aria-hidden="true">
                      <hr />
                    </li>
                  )}
                  <li data-testid="cart-backend-addon">
                    <Layout.Flex gap="x-large">
                      <Layout.Fill>
                        <h5 className={styles['cart-item-name']}>{addon.title}</h5>
                      </Layout.Fill>
                      <div className={styles['cart-item-price']}>
                        {`${value} ${getDisplayCurrency({ paymentOption, value })}${hasCaveats ? '*' : ''}`}
                      </div>
                    </Layout.Flex>
                  </li>
                </Fragment>
              );
            })}
          </Layout.Flex.Column>
        </div>
      )}

      {(displayedOptionalAddons?.length > 0 || promos?.length > 0) && (
        <div>
          {displayedOptionalAddons?.length > 0 && (
            <Addons
              addons={displayedOptionalAddons}
              onAddonChange={onAddonChange}
              paymentOption={paymentOption}
            />
          )}

          {promos?.length > 0 && (
            <ul>
              {promos.map((promo) => (
                <li className={styles['cart-promo']} key={promo.name}>
                  <strong>{promo.name}</strong>
                  <span className={styles['cart-promo-amount']}>
                    -{promo[paymentOption.currency]}{' '}
                    {getDisplayCurrency({
                      paymentOption,
                      value: promo[paymentOption.currency],
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={styles['cart-total-row']}>
        <strong>{totalText}</strong>
        <strong>{`${totalDue} ${getDisplayCurrency({ paymentOption, value: totalDue })}${hasCaveats ? '*' : ''}`}</strong>
      </div>
      {footnote && (
        <div aria-label="Additional information" className={styles['cart-footnote']}>
          {footnote}
        </div>
      )}
    </Layout.Flex.Column>
  );
};

Cart.propTypes = {
  addons: PropTypes.arrayOf(
    PropTypes.shape({
      addonType: PropTypes.number,
      description: PropTypes.node,
      id: PropTypes.number,
      prices: PropTypes.objectOf(PropTypes.number).isRequired,
      title: PropTypes.node.isRequired,
    })
  ),
  footnote: PropTypes.node,
  hasCaveats: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.node,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.node.isRequired,
      prices: PropTypes.objectOf(PropTypes.number).isRequired,
    })
  ).isRequired,
  onAddonChange: PropTypes.func,
  optionalAddons: PropTypes.arrayOf(
    PropTypes.shape({
      addonType: PropTypes.number,
      description: PropTypes.node,
      id: PropTypes.number,
      prices: PropTypes.objectOf(PropTypes.number).isRequired,
      selected: PropTypes.bool,
      title: PropTypes.node.isRequired,
    })
  ),
  paymentOption: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    currencySingular: PropTypes.string,
    key: PropTypes.oneOf(['credits', 'credit_card', 'subscription']).isRequired,
  }).isRequired,
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      completes: PropTypes.number,
      credits: PropTypes.number,
      name: PropTypes.string.isRequired,
    })
  ),
  totalText: PropTypes.node,
};

export default Cart;
